
import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { io } from 'socket.io-client';
import axios from 'axios';
import styles from './WhiteboardPage.module.css';
import { useAuth } from '../../contexts/AuthContext';

// URLs - usar variáveis de ambiente ou caminhos relativos
const API_BASE = import.meta.env.VITE_API_URL || '/api';
const API_URL = `${API_BASE}/whiteboard`;
const SOCKET_URL = window.location.origin;
const UPLOAD_BASE = window.location.origin;

// Cores para sticky notes
const STICKY_COLORS = ['#fff740', '#a7ffeb', '#f8bbd0', '#ccff90', '#ffd180'];

// Cor estável por usuário (cursor + avatar) e iniciais (presença de colaboração)
const PRESENCE_COLORS = ['#ef4444', '#f97316', '#eab308', '#22c55e', '#06b6d4', '#3b82f6', '#8b5cf6', '#ec4899'];
const userColor = (key) => { const s = String(key || '?'); let h = 0; for (let i = 0; i < s.length; i++) h = (h * 31 + s.charCodeAt(i)) >>> 0; return PRESENCE_COLORS[h % PRESENCE_COLORS.length]; };
const initials = (name) => { const p = String(name || 'U').trim().split(/\s+/); return ((p[0] && p[0][0] || 'U') + (p.length > 1 ? p[p.length - 1][0] : '')).toUpperCase(); };

// Ícones SVG (toolbar estilo Miro) — substituem os emojis
const svgBase = { width: 20, height: 20, viewBox: '0 0 24 24', fill: 'none', stroke: 'currentColor', strokeWidth: 2, strokeLinecap: 'round', strokeLinejoin: 'round' };
const Icon = {
    select: () => (<svg {...svgBase}><path d="M4 3l6.5 16 2.2-6.3L19 10.5 4 3z" /></svg>),
    hand: () => (<svg {...svgBase}><path d="M7 11V6a1.5 1.5 0 0 1 3 0v4m0 0V4.5a1.5 1.5 0 0 1 3 0V10m0 0V6a1.5 1.5 0 0 1 3 0v7a6 6 0 0 1-6 6h-1a5 5 0 0 1-3.5-1.5L4 14.5a1.6 1.6 0 0 1 2.3-2.2L8 14" /></svg>),
    sticky: () => (<svg {...svgBase}><path d="M4 4h16v10l-6 6H4z" /><path d="M20 14h-6v6" /></svg>),
    text: () => (<svg {...svgBase}><path d="M5 6V5h14v1M12 5v14M9 19h6" /></svg>),
    square: () => (<svg {...svgBase}><rect x="4" y="4" width="16" height="16" rx="2" /></svg>),
    circle: () => (<svg {...svgBase}><circle cx="12" cy="12" r="9" /></svg>),
    diamond: () => (<svg {...svgBase}><path d="M12 3l8.5 9-8.5 9-8.5-9z" /></svg>),
    triangle: () => (<svg {...svgBase}><path d="M12 4l8.5 15.5h-17z" /></svg>),
    shapes: () => (<svg {...svgBase}><rect x="3" y="3" width="8" height="8" rx="1" /><circle cx="16.5" cy="16" r="4.5" /></svg>),
    image: () => (<svg {...svgBase}><rect x="3" y="4" width="18" height="16" rx="2" /><circle cx="8.5" cy="9.5" r="1.5" /><path d="M21 16l-5-5L5 20" /></svg>),
    connector: () => (<svg {...svgBase}><circle cx="5" cy="19" r="1.8" /><circle cx="19" cy="5" r="1.8" /><path d="M6.4 17.6 L17.6 6.4" /></svg>),
    frame: () => (<svg {...svgBase}><rect x="4" y="6" width="16" height="13" rx="1" /><path d="M4 10h16" /></svg>),
    grid: () => (<svg {...svgBase} width="16" height="16"><rect x="3" y="3" width="7" height="7" rx="1" /><rect x="14" y="3" width="7" height="7" rx="1" /><rect x="3" y="14" width="7" height="7" rx="1" /><rect x="14" y="14" width="7" height="7" rx="1" /></svg>),
    minus: () => (<svg {...svgBase}><path d="M5 12h14" /></svg>),
    plus: () => (<svg {...svgBase}><path d="M12 5v14M5 12h14" /></svg>),
    fit: () => (<svg {...svgBase}><path d="M4 9V4h5M20 9V4h-5M4 15v5h5M20 15v5h-5" /></svg>),
    undo: () => (<svg {...svgBase}><path d="M9 7L4 12l5 5" /><path d="M4 12h11a5 5 0 0 1 0 10h-2" /></svg>),
    redo: () => (<svg {...svgBase}><path d="M15 7l5 5-5 5" /><path d="M20 12H9a5 5 0 0 0 0 10h2" /></svg>),
};

const WhiteboardPage = () => {
    const { boardId } = useParams();
    const navigate = useNavigate();
    const { user } = useAuth();
    const lastCursorEmitRef = useRef(0);

    // Estado
    const [boards, setBoards] = useState([]);
    const [currentBoard, setCurrentBoard] = useState(null);
    const [elements, setElements] = useState([]);
    const [loading, setLoading] = useState(true);
    const [scale, setScale] = useState(1);
    const [offset, setOffset] = useState({ x: 0, y: 0 });
    const [isPanning, setIsPanning] = useState(false);
    const [panStart, setPanStart] = useState({ x: 0, y: 0 });
    const [activeTool, setActiveTool] = useState('select'); // select, pan, sticky, text, square, circle, arrow, draw
    const [selectedIds, setSelectedIds] = useState([]);
    const selectedElementId = selectedIds.length === 1 ? selectedIds[0] : null; // único selecionado (handles/cor)
    const [selectionRect, setSelectionRect] = useState(null); // caixa de seleção (marquee), coords do board
    const [resizeCorner, setResizeCorner] = useState('se');
    const isSelectingRef = useRef(false);
    const selStartRef = useRef({ x: 0, y: 0 });
    const selRectRef = useRef(null);
    const dragGroupStartRef = useRef({});
    const dragGroupBBoxRef = useRef(null);
    const snapTargetsRef = useRef(null);
    const dragMovingIdsRef = useRef([]); // ids realmente movidos (frame leva os filhos junto)
    const [guides, setGuides] = useState({ x: null, y: null }); // linhas-guia de alinhamento (coords do board)
    const [connectFrom, setConnectFrom] = useState(null); // ferramenta conector: id da forma de origem
    const [shapesMenuOpen, setShapesMenuOpen] = useState(false); // flyout "Formas e linhas"
    const [connectMouse, setConnectMouse] = useState(null); // posição do mouse (board) p/ preview do conector
    const elementsRef = useRef([]); // sempre os elementos atuais (evita closure stale em handlers)
    const tidyStateRef = useRef({ key: '', rows: 0 }); // ciclo de linhas do "Organizar" (1 → 2 → 3 → ...)
    const mouseClientRef = useRef(null); // última posição do cursor (clientX/Y) p/ colar imagem no lugar certo

    // ---- Histórico (Desfazer / Refazer) ----
    const histRef = useRef({ past: [], future: [] });
    const pendingBeforeRef = useRef(null); // snapshot "antes" capturado no início de drag/resize
    const [histVersion, setHistVersion] = useState(0); // força re-render dos botões
    const cloneEls = (els) => els.map(e => ({ ...e, content: e.content ? JSON.parse(JSON.stringify(e.content)) : {} }));
    const elsChanged = (a, b) => a.x !== b.x || a.y !== b.y || a.width !== b.width || a.height !== b.height || JSON.stringify(a.content || {}) !== JSON.stringify(b.content || {});
    // Empilha o estado ATUAL como ponto de desfazer (chamar ANTES de uma mutação instantânea)
    const pushUndo = () => { const h = histRef.current; h.past.push(cloneEls(elementsRef.current)); if (h.past.length > 60) h.past.shift(); h.future = []; setHistVersion(v => v + 1); };
    // Empilha um snapshot "antes" só se de fato mudou (p/ drag/resize)
    const pushUndoIfChanged = (before) => { if (!before) return; if (JSON.stringify(before) === JSON.stringify(elementsRef.current)) return; const h = histRef.current; h.past.push(before); if (h.past.length > 60) h.past.shift(); h.future = []; setHistVersion(v => v + 1); };
    // Reconcilia o backend p/ bater com `target` (cria/atualiza/deleta); devolve a lista final (IDs reais) + remap de IDs
    const reconcileTo = async (current, target) => {
        const curMap = new Map(current.map(e => [e.id, e]));
        const tgtIds = new Set(target.map(e => e.id));
        for (const e of current) {
            if (!tgtIds.has(e.id)) {
                try { await axios.delete(`${API_URL}/elements/${e.id}`); if (socketRef.current) socketRef.current.emit('element-delete', { boardId, elementId: e.id }); } catch (_) { }
            }
        }
        const idRemap = {};
        const finalEls = [];
        for (const e of target) {
            const cur = curMap.get(e.id);
            if (!cur) {
                try {
                    const res = await axios.post(`${API_URL}/elements`, { board_id: boardId, type: e.type, x: e.x, y: e.y, width: e.width, height: e.height, z_index: e.z_index || 0, content: e.content || {} });
                    const created = { ...res.data.data, content: e.content || {} };
                    idRemap[e.id] = created.id;
                    if (socketRef.current) socketRef.current.emit('element-create', { boardId, element: created });
                    finalEls.push(created);
                } catch (_) { finalEls.push(e); }
            } else {
                if (elsChanged(cur, e)) {
                    try { await axios.put(`${API_URL}/elements/${e.id}`, { x: e.x, y: e.y, width: e.width, height: e.height, content: e.content || {} }); if (socketRef.current) socketRef.current.emit('element-update', { boardId, element: e }); } catch (_) { }
                }
                finalEls.push(e);
            }
        }
        return { finalEls, idRemap };
    };
    // Re-criar muda o ID → remapeia nos snapshots do histórico (e nos conectores) p/ manter consistência
    const applyRemapToHistory = (idRemap) => {
        if (!Object.keys(idRemap).length) return;
        const fix = (els) => els.map(e => {
            const ne = { ...e, id: idRemap[e.id] || e.id };
            if (ne.type === 'connector' && ne.content) {
                ne.content = { ...ne.content };
                if (idRemap[ne.content.fromId]) ne.content.fromId = idRemap[ne.content.fromId];
                if (idRemap[ne.content.toId]) ne.content.toId = idRemap[ne.content.toId];
            }
            return ne;
        });
        const h = histRef.current;
        h.past = h.past.map(fix);
        h.future = h.future.map(fix);
    };
    const undo = async () => {
        const h = histRef.current;
        if (!h.past.length) return;
        const target = h.past.pop();
        h.future.push(cloneEls(elementsRef.current));
        setSelectedIds([]);
        const { finalEls, idRemap } = await reconcileTo(elementsRef.current, target);
        applyRemapToHistory(idRemap);
        setElements(finalEls);
        setHistVersion(v => v + 1);
    };
    const redo = async () => {
        const h = histRef.current;
        if (!h.future.length) return;
        const target = h.future.pop();
        h.past.push(cloneEls(elementsRef.current));
        setSelectedIds([]);
        const { finalEls, idRemap } = await reconcileTo(elementsRef.current, target);
        applyRemapToHistory(idRemap);
        setElements(finalEls);
        setHistVersion(v => v + 1);
    };
    const [isDrawing, setIsDrawing] = useState(false);
    const [currentPath, setCurrentPath] = useState(null);
    const [remoteCursors, setRemoteCursors] = useState({});
    const [socket, setSocket] = useState(null);
    const [isDragging, setIsDragging] = useState(false);
    const [dragStart, setDragStart] = useState({ x: 0, y: 0 });
    const [dragElementStart, setDragElementStart] = useState({ x: 0, y: 0 });
    const [isResizing, setIsResizing] = useState(false);
    const [resizeStart, setResizeStart] = useState({ x: 0, y: 0 });
    const [resizeElementStart, setResizeElementStart] = useState({ width: 0, height: 0 });
    const [defaultColor, setDefaultColor] = useState('#ffffff'); // Cor padrão para novos shapes

    // Mudar cor do elemento selecionado
    const changeElementColor = (color) => {
        setDefaultColor(color);
        const colorable = selectedIds.some(id => { const el = elements.find(e => e.id === id); return el && (el.type === 'shape' || el.type === 'sticky'); });
        if (colorable) pushUndo();
        selectedIds.forEach(id => {
            const el = elements.find(e => e.id === id);
            if (el && (el.type === 'shape' || el.type === 'sticky')) {
                const newContent = { ...el.content, color };
                updateLocalElement(el.id, { content: newContent });
                saveElement({ ...el, content: newContent });
            }
        });
    };

    // Manter elementsRef/selectedIdsRef sempre atuais + clipboard interno (handlers async leem daqui, sem closure stale)
    const clipboardRef = useRef([]);
    const selectedIdsRef = useRef([]);
    useEffect(() => { elementsRef.current = elements; selectedIdsRef.current = selectedIds; });

    // Rastrear a posição do cursor (pra colar imagem exatamente onde ele está)
    useEffect(() => {
        const onMove = (e) => { mouseClientRef.current = { x: e.clientX, y: e.clientY }; };
        document.addEventListener('mousemove', onMove);
        return () => document.removeEventListener('mousemove', onMove);
    }, []);

    // Refs
    const canvasRef = useRef(null);
    const fileInputRef = useRef(null);
    const socketRef = useRef(null);

    // Inicializar Socket e Carregar Dados
    useEffect(() => {
        if (!boardId) {
            loadBoards();
            setLoading(false);
            return;
        }

        // Carregar Board
        loadBoardData(boardId);

        // Conectar Socket
        const newSocket = io(`${SOCKET_URL}/whiteboard`);
        socketRef.current = newSocket;
        setSocket(newSocket);

        newSocket.on('connect', () => {
            console.log('🔗 Conectado ao servidor de Whiteboard');
            newSocket.emit('join-board', boardId);
        });

        // Listeners de eventos
        newSocket.on('element-created', (element) => {
            setElements(prev => [...prev, element]);
        });

        newSocket.on('element-updated', (updatedElement) => {
            setElements(prev => prev.map(el => el.id === updatedElement.id ? updatedElement : el));
        });

        newSocket.on('element-deleted', (elementId) => {
            setElements(prev => prev.filter(el => el.id !== elementId));
        });

        newSocket.on('cursor-moved', ({ userId, user, x, y }) => {
            setRemoteCursors(prev => ({
                ...prev,
                [userId]: { user, x, y, lastUpdate: Date.now() }
            }));
        });

        return () => {
            newSocket.disconnect();
        };
    }, [boardId]);

    // Limpar cursores inativos
    useEffect(() => {
        const interval = setInterval(() => {
            const now = Date.now();
            setRemoteCursors(prev => {
                const next = { ...prev };
                let changed = false;
                Object.keys(next).forEach(key => {
                    if (now - next[key].lastUpdate > 5000) { // 5 segundos inativo
                        delete next[key];
                        changed = true;
                    }
                });
                return changed ? next : prev;
            });
        }, 1000);
        return () => clearInterval(interval);
    }, []);

    // Colar imagem do clipboard (Ctrl+V)
    useEffect(() => {
        if (!boardId) return;

        const handlePaste = async (e) => {
            const items = e.clipboardData?.items;
            let imageHandled = false;

            if (items) for (const item of items) {
                if (item.type.startsWith('image/')) {
                    imageHandled = true;
                    e.preventDefault();
                    const file = item.getAsFile();
                    if (!file) continue;

                    // Upload da imagem
                    const formData = new FormData();
                    formData.append('image', file);
                    formData.append('board_id', boardId);

                    try {
                        const res = await axios.post(`${API_URL}/upload`, formData);
                        const { filename, url } = res.data.data;

                        // Posição = exatamente onde está o cursor no quadro (centralizada nele); fallback = centro da tela
                        let bx, by;
                        if (mouseClientRef.current && canvasRef.current) {
                            const p = toBoardCoords(mouseClientRef.current.x, mouseClientRef.current.y);
                            bx = p.x; by = p.y;
                        } else {
                            bx = (-offset.x + window.innerWidth / 2) / scale;
                            by = (-offset.y + window.innerHeight / 2) / scale;
                        }

                        const newElement = {
                            board_id: boardId,
                            type: 'image',
                            x: bx - 150,
                            y: by - 150,
                            width: 300,
                            height: 300,
                            content: { filename, url }
                        };

                        // Criar elemento
                        const createRes = await axios.post(`${API_URL}/elements`, newElement);
                        const created = { ...createRes.data.data, content: newElement.content };
                        pushUndo();
                        setElements(prev => [...prev, created]);

                        if (socketRef.current) {
                            socketRef.current.emit('element-create', { boardId, element: created });
                        }

                        console.log('📋 Imagem colada com sucesso!');
                    } catch (err) {
                        console.error('Erro ao colar imagem:', err);
                        alert('Erro ao colar imagem');
                    }
                    break;
                }
            }

            // Sem imagem no clipboard do sistema → cola os elementos copiados internamente (duplicar)
            if (!imageHandled && clipboardRef.current.length) { e.preventDefault(); await pasteClipboard(); }
        };

        document.addEventListener('paste', handlePaste);
        return () => document.removeEventListener('paste', handlePaste);
    }, [boardId, offset, scale]);

    // Deletar elemento com tecla Delete/Backspace
    useEffect(() => {
        if (!boardId) return;

        const handleKeyDown = async (e) => {
            // Ignorar se estiver editando texto
            if (e.target.tagName === 'INPUT' || e.target.tagName === 'TEXTAREA') return;

            // Desfazer (Ctrl/Cmd+Z) e Refazer (Ctrl/Cmd+Y ou Ctrl/Cmd+Shift+Z)
            if ((e.ctrlKey || e.metaKey) && (e.key === 'z' || e.key === 'Z') && !e.shiftKey) { e.preventDefault(); await undo(); return; }
            if ((e.ctrlKey || e.metaKey) && ((e.key === 'y' || e.key === 'Y') || ((e.key === 'z' || e.key === 'Z') && e.shiftKey))) { e.preventDefault(); await redo(); return; }

            // Copiar (Ctrl/Cmd+C) e Duplicar (Ctrl/Cmd+D)
            if ((e.ctrlKey || e.metaKey) && (e.key === 'c' || e.key === 'C')) { copySelection(); return; }
            if ((e.ctrlKey || e.metaKey) && (e.key === 'd' || e.key === 'D')) { e.preventDefault(); await duplicateSelection(); return; }

            if ((e.key === 'Delete' || e.key === 'Backspace') && selectedIds.length) {
                e.preventDefault();
                const ids = [...selectedIds];
                // Também remove conectores ligados aos elementos apagados (evita órfãos / linha pendurada)
                const connIds = elementsRef.current
                    .filter(el => el.type === 'connector' && el.content && (ids.includes(el.content.fromId) || ids.includes(el.content.toId)))
                    .map(el => el.id);
                const allIds = Array.from(new Set([...ids, ...connIds]));
                pushUndo();
                setElements(prev => prev.filter(el => !allIds.includes(el.id)));
                setSelectedIds([]);
                for (const id of allIds) {
                    try {
                        await axios.delete(`${API_URL}/elements/${id}`);
                        if (socketRef.current) socketRef.current.emit('element-delete', { boardId, elementId: id });
                    } catch (err) {
                        console.error('Erro ao deletar elemento:', err);
                    }
                }
            }
        };

        document.addEventListener('keydown', handleKeyDown);
        return () => document.removeEventListener('keydown', handleKeyDown);
    }, [boardId, selectedIds]);

    // Atalhos de ferramenta (estilo Miro): V/S=selecionar, H/Espaço=mover, N=sticky, T=texto, R=quadrado, C=círculo, I=imagem
    useEffect(() => {
        if (!boardId) return;
        const onKey = (e) => {
            if (e.target.tagName === 'INPUT' || e.target.tagName === 'TEXTAREA') return;
            if (e.ctrlKey || e.metaKey || e.altKey) return;
            if (e.key === ' ') { e.preventDefault(); setActiveTool('pan'); return; }
            if (e.key === 'i' || e.key === 'I') { fileInputRef.current?.click(); return; }
            if (e.key === 'Escape') { setConnectFrom(null); setActiveTool('select'); return; }
            const map = { v: 'select', s: 'select', h: 'pan', n: 'sticky', t: 'text', r: 'square', o: 'circle', c: 'circle', l: 'connector', f: 'frame' };
            const tool = map[e.key.toLowerCase()];
            if (tool) setActiveTool(tool);
        };
        document.addEventListener('keydown', onKey);
        return () => document.removeEventListener('keydown', onKey);
    }, [boardId]);

    // Refs para acesso dentro do EventListener sem recriá-lo
    const scaleRef = useRef(scale);
    const offsetRef = useRef(offset);
    useEffect(() => { scaleRef.current = scale; offsetRef.current = offset; }, [scale, offset]);

    // Estado para detectar quando o canvas está montado
    const [canvasMounted, setCanvasMounted] = useState(false);
    const [wrapperSize, setWrapperSize] = useState({ w: 0, h: 0 }); // tamanho do viewport (minimapa)

    // Callback ref para detectar quando o canvas é montado/desmontado
    const canvasRefCallback = useCallback((node) => {
        canvasRef.current = node;
        setCanvasMounted(!!node);
    }, []);

    // Medir o tamanho do viewport do canvas (pro minimapa e centralização)
    useEffect(() => {
        if (!canvasMounted || !canvasRef.current) return;
        const measure = () => { const el = canvasRef.current; if (el) setWrapperSize({ w: el.clientWidth, h: el.clientHeight }); };
        measure();
        window.addEventListener('resize', measure);
        return () => window.removeEventListener('resize', measure);
    }, [canvasMounted]);

    // Zoom/Pan com Wheel Global (Intercepta navegador)
    useEffect(() => {
        // Só registra o listener quando o canvas estiver montado
        if (!canvasMounted) {
            console.log('🟡 Whiteboard: Aguardando canvas ser montado...');
            return;
        }

        const canvas = canvasRef.current;
        if (!canvas) {
            console.log('🔴 Whiteboard: Canvas ref não encontrado após montagem');
            return;
        }

        console.log('🟢 Whiteboard: Registrando listener de zoom no document');

        const onWheel = (e) => {
            // Verificar se o mouse está sobre o canvas
            const rect = canvas.getBoundingClientRect();
            const isOverCanvas = (
                e.clientX >= rect.left &&
                e.clientX <= rect.right &&
                e.clientY >= rect.top &&
                e.clientY <= rect.bottom
            );

            if (!isOverCanvas) return;

            // Verificar tecla pressionada (Ctrl ou Meta/Command)
            if (e.ctrlKey || e.metaKey) {
                // BLOQUEIO CRÍTICO: Previne zoom do navegador
                e.preventDefault();
                e.stopPropagation();
                e.stopImmediatePropagation();

                const zoomSensitivity = 0.002; // Sensibilidade aumentada para melhor resposta
                const currentScale = scaleRef.current;
                const currentOffset = offsetRef.current;

                // Captura posição do mouse relativo ao canvas
                const mouseX = e.clientX - rect.left;
                const mouseY = e.clientY - rect.top;

                // Ponto no mundo (antes do zoom)
                const worldX = (mouseX - currentOffset.x) / currentScale;
                const worldY = (mouseY - currentOffset.y) / currentScale;

                // Calcular novo Scale
                let newScale = currentScale - e.deltaY * zoomSensitivity;
                newScale = Math.min(Math.max(0.1, newScale), 5);

                // Calcular novo Offset para manter o ponto sob o mouse
                const newOffsetX = mouseX - worldX * newScale;
                const newOffsetY = mouseY - worldY * newScale;

                console.log(`🔍 Zoom: ${Math.round(currentScale * 100)}% → ${Math.round(newScale * 100)}%`);

                setScale(newScale);
                setOffset({ x: newOffsetX, y: newOffsetY });

                return false;
            } else {
                // Comportamento sem Ctrl - Pan com ferramenta pan
                if (activeTool === 'pan') {
                    e.preventDefault();
                    setOffset(prev => ({
                        x: prev.x - e.deltaX,
                        y: prev.y - e.deltaY
                    }));
                }
            }
        };

        // Adicionar ao document com capture: true para interceptar ANTES do navegador
        document.addEventListener('wheel', onWheel, { passive: false, capture: true });

        return () => {
            console.log('🔴 Whiteboard: Removendo listener de zoom');
            document.removeEventListener('wheel', onWheel, { capture: true });
        };
    }, [activeTool, canvasMounted]);

    // API Call Helpers
    const loadBoards = async () => {
        try {
            const res = await axios.get(`${API_URL}/boards`);
            setBoards(res.data.data);
        } catch (err) {
            console.error('Erro ao carregar boards:', err);
        }
    };

    const loadBoardData = async (id) => {
        setLoading(true);
        try {
            const res = await axios.get(`${API_URL}/boards/${id}`);
            setCurrentBoard(res.data.data);
            // Parse JSON content
            const parsedElements = res.data.data.elements.map(el => ({
                ...el,
                content: typeof el.content === 'string' ? JSON.parse(el.content) : el.content
            }));
            setElements(parsedElements);
        } catch (err) {
            console.error('Erro ao carregar board:', err);
            navigate('/quadro-branco');
        } finally {
            setLoading(false);
        }
    };

    const createBoard = async () => {
        try {
            const name = prompt('Nome do novo quadro:', 'Novo Quadro de Planejamento');
            if (!name) return;

            const res = await axios.post(`${API_URL}/boards`, {
                name,
                description: 'Criado via Web'
            });

            const newBoard = res.data.data;
            navigate(`/quadro-branco/${newBoard.id}`);
        } catch (err) {
            console.error('Erro ao criar board:', err);
            alert('Erro ao criar quadro');
        }
    };

    const deleteBoard = async (e, id) => {
        e.stopPropagation();
        if (!window.confirm('Tem certeza que deseja excluir este quadro?')) return;

        try {
            await axios.delete(`${API_URL}/boards/${id}`);
            loadBoards();
        } catch (err) {
            console.error('Erro ao deletar board:', err);
        }
    };

    // Canvas Interactions


    const toBoardCoords = (clientX, clientY) => {
        if (!canvasRef.current) return { x: 0, y: 0 };
        const rect = canvasRef.current.getBoundingClientRect();
        return {
            x: (clientX - rect.left - offset.x) / scale,
            y: (clientY - rect.top - offset.y) / scale
        };
    };

    const handleMouseDown = (e) => {
        setShapesMenuOpen(false); // fecha o flyout de formas ao interagir com o canvas
        const cls = e.target.className;
        const onBackground = e.target === canvasRef.current || (typeof cls === 'string' && cls.includes(styles.canvasContent));

        // Ferramenta conector: clique no fundo cancela a ligação em curso (cliques em formas vão pelo startDrag)
        if (activeTool === 'connector') {
            if (onBackground && e.button === 0) setConnectFrom(null);
            return;
        }

        if (activeTool === 'pan' || (activeTool === 'select' && e.button === 1)) {
            setIsPanning(true);
            setPanStart({ x: e.clientX, y: e.clientY });
            return;
        }

        // Clique no fundo com a ferramenta de seleção → inicia caixa de seleção (marquee)
        if (activeTool === 'select' && onBackground && e.button === 0) {
            setSelectedIds([]);
            const p = toBoardCoords(e.clientX, e.clientY);
            isSelectingRef.current = true;
            selStartRef.current = p;
            selRectRef.current = { x: p.x, y: p.y, w: 0, h: 0 };
            setSelectionRect(selRectRef.current);
            return;
        }

        const { x, y } = toBoardCoords(e.clientX, e.clientY);

        // Create Element Logic
        if (activeTool !== 'select' && activeTool !== 'pan' && activeTool !== 'draw' && activeTool !== 'connector') {
            let newElement = {
                board_id: boardId,
                type: activeTool,
                x,
                y,
                width: 100,
                height: 100,
                z_index: elements.length + 1,
                content: {}
            };

            if (activeTool === 'sticky') {
                newElement.width = 200;
                newElement.height = 200;
                newElement.content = { text: '', color: STICKY_COLORS[Math.floor(Math.random() * STICKY_COLORS.length)] };
            } else if (activeTool === 'text') {
                newElement.width = 200;
                newElement.height = 50;
                newElement.content = { text: 'Clique para editar' };
            } else if (activeTool === 'circle') {
                newElement.type = 'shape';
                newElement.content = { shape: 'circle', color: defaultColor, border: '#000' };
            } else if (activeTool === 'square') {
                newElement.type = 'shape';
                newElement.content = { shape: 'rectangle', color: defaultColor, border: '#000' };
            } else if (activeTool === 'diamond') {
                newElement.type = 'shape';
                newElement.content = { shape: 'diamond', color: defaultColor, border: '#000' };
            } else if (activeTool === 'triangle') {
                newElement.type = 'shape';
                newElement.content = { shape: 'triangle', color: defaultColor, border: '#000' };
            } else if (activeTool === 'frame') {
                newElement.width = 480;
                newElement.height = 320;
                newElement.content = { title: 'Frame' };
            }

            createElement(newElement);
            setActiveTool('select');
        }
    };

    // ---- Copiar / Colar / Duplicar ----
    // Best-effort: copia a imagem (same-origin) pro clipboard do SISTEMA, pra colar em outro app
    const copyImageToClipboard = async (url) => {
        try {
            if (!navigator.clipboard || !window.ClipboardItem) return;
            const img = new Image();
            img.crossOrigin = 'anonymous';
            await new Promise((ok, fail) => { img.onload = ok; img.onerror = fail; img.src = url; });
            const cv = document.createElement('canvas');
            cv.width = img.naturalWidth; cv.height = img.naturalHeight;
            cv.getContext('2d').drawImage(img, 0, 0);
            const blob = await new Promise(res => cv.toBlob(res, 'image/png'));
            if (blob) await navigator.clipboard.write([new window.ClipboardItem({ 'image/png': blob })]);
        } catch (e) { /* best-effort */ }
    };

    const copySelection = () => {
        const sel = elementsRef.current.filter(el => selectedIdsRef.current.includes(el.id) && el.type !== 'connector');
        if (!sel.length) return;
        clipboardRef.current = sel.map(el => ({ type: el.type, x: el.x, y: el.y, width: el.width, height: el.height, content: JSON.parse(JSON.stringify(el.content || {})) }));
        // Clipboard do SISTEMA (pra colar em outro sistema): texto dos sticky/textos, ou imagem única
        const text = sel.map(el => (el.content && el.content.text) || '').filter(Boolean).join('\n');
        if (text && navigator.clipboard) navigator.clipboard.writeText(text).catch(() => {});
        if (sel.length === 1 && sel[0].type === 'image' && sel[0].content && sel[0].content.url) copyImageToClipboard(`${UPLOAD_BASE}${sel[0].content.url}`);
    };

    const pasteClipboard = async () => {
        const src = clipboardRef.current;
        if (!src || !src.length) return;
        pushUndo();
        const newIds = [];
        for (const s of src) {
            const newEl = { board_id: boardId, type: s.type, x: s.x + 24, y: s.y + 24, width: s.width, height: s.height, z_index: 0, content: JSON.parse(JSON.stringify(s.content || {})) };
            try {
                const res = await axios.post(`${API_URL}/elements`, newEl);
                const created = { ...res.data.data, content: newEl.content };
                setElements(prev => [...prev, created]);
                if (socketRef.current) socketRef.current.emit('element-create', { boardId, element: created });
                newIds.push(created.id);
            } catch (err) { console.error('Erro ao colar:', err); }
        }
        // cascata: a próxima colagem desloca mais (não empilha exatamente em cima)
        clipboardRef.current = src.map(s => ({ ...s, x: s.x + 24, y: s.y + 24 }));
        if (newIds.length) setSelectedIds(newIds);
    };

    const duplicateSelection = async () => { copySelection(); await pasteClipboard(); };

    const createElement = async (elementData) => {
        pushUndo();
        try {
            const res = await axios.post(`${API_URL}/elements`, elementData);
            const created = { ...res.data.data, content: elementData.content };

            setElements(prev => [...prev, created]);
            if (socketRef.current) {
                socketRef.current.emit('element-create', { boardId, element: created });
            }
        } catch (err) {
            console.error('Erro ao criar elemento:', err);
        }
    };

    // Organizar (tidy up): alinha itens num bloco organizado.
    // - Se um FRAME estiver na seleção → alinha DENTRO do frame (no topo, com margem, quebrando na largura dele) = o "ambiente" do dono.
    // - Senão → alinha no CENTRO do que está visível na tela (viewport), não mais no canto.
    const gap = 28;
    // Posiciona os itens em grade a partir de (ox,oy), quebra ao passar de maxW; devolve posições + tamanho do bloco
    const layoutGrid = (items, ox, oy, maxW) => {
        let cx = ox, cy = oy, rowH = 0, bw = 0, bh = 0;
        const pos = {};
        items.forEach(el => {
            if (cx > ox && (cx - ox) + el.width > maxW) { cx = ox; cy += rowH + gap; rowH = 0; }
            pos[el.id] = { x: cx, y: cy };
            cx += el.width + gap;
            rowH = Math.max(rowH, el.height);
            bw = Math.max(bw, (cx - gap) - ox);
            bh = Math.max(bh, (cy + rowH) - oy);
        });
        return { pos, bw, bh };
    };

    // Posiciona com N itens por linha (perRow), empacotando à esquerda; devolve posições + tamanho do bloco
    const layoutRowsByCount = (items, ox, oy, perRow) => {
        let cx = ox, cy = oy, rowH = 0, bw = 0;
        const pos = {};
        items.forEach((el, i) => {
            if (i > 0 && i % perRow === 0) { cx = ox; cy += rowH + gap; rowH = 0; }
            pos[el.id] = { x: cx, y: cy };
            cx += el.width + gap;
            rowH = Math.max(rowH, el.height);
            bw = Math.max(bw, (cx - gap) - ox);
        });
        return { pos, bw, bh: (cy + rowH) - oy };
    };

    const tidyUp = async () => {
        const selectedEls = elements.filter(el => selectedIds.includes(el.id));
        const frame = selectedEls.find(el => el.type === 'frame');
        let posMap;

        if (frame) {
            tidyStateRef.current = { key: '', rows: 0 }; // sai do ciclo de linhas ao organizar dentro de frame
            const pad = 28;
            const selNonFrame = selectedEls.filter(el => el.id !== frame.id && el.type !== 'connector' && el.type !== 'frame');
            // frame + fotos selecionadas → organiza as fotos; só o frame → organiza o que já está dentro dele
            const items = selNonFrame.length ? selNonFrame : elements.filter(el => el.type !== 'connector' && el.type !== 'frame' && isInsideFrame(el, frame));
            if (items.length < 1) return;
            const sorted = [...items].sort((a, b) => (Math.abs(a.y - b.y) > 60 ? a.y - b.y : a.x - b.x));
            posMap = layoutGrid(sorted, frame.x + pad, frame.y + pad, frame.width - pad * 2).pos;
        } else {
            const sel = selectedEls.filter(el => el.type !== 'connector' && el.type !== 'frame');
            const items = sel.length >= 2 ? sel : elements.filter(el => el.type !== 'connector' && el.type !== 'frame');
            if (items.length < 2) return;
            const sorted = [...items].sort((a, b) => (Math.abs(a.y - b.y) > 60 ? a.y - b.y : a.x - b.x));
            // Cliques repetidos no MESMO conjunto ciclam o nº de linhas: 1 → 2 → 3 → ... (volta a 1 depois do máximo)
            const key = sorted.map(e => e.id).join(',');
            let rows = (tidyStateRef.current.key === key) ? tidyStateRef.current.rows + 1 : 1;
            if (rows < 1 || rows > sorted.length) rows = 1;
            tidyStateRef.current = { key, rows };
            const perRow = Math.ceil(sorted.length / rows);
            const measured = layoutRowsByCount(sorted, 0, 0, perRow);
            const vcx = (-offset.x + (wrapperSize.w || 1200) / 2) / scale;
            const vcy = (-offset.y + (wrapperSize.h || 700) / 2) / scale;
            posMap = layoutRowsByCount(sorted, vcx - measured.bw / 2, vcy - measured.bh / 2, perRow).pos;
        }

        pushUndo();
        setElements(prev => prev.map(el => posMap[el.id] ? { ...el, x: posMap[el.id].x, y: posMap[el.id].y } : el));
        for (const id of Object.keys(posMap)) {
            const p = posMap[id];
            try {
                await axios.put(`${API_URL}/elements/${id}`, { x: p.x, y: p.y });
                const elem = elementsRef.current.find(e => String(e.id) === String(id));
                if (socketRef.current && elem) socketRef.current.emit('element-update', { boardId, element: { ...elem, x: p.x, y: p.y } });
            } catch (err) { console.error('Erro ao organizar:', err); }
        }
    };

    const handleMouseMove = (e) => {
        // Broadcast cursor position
        if (socketRef.current && boardId) {
            const now = Date.now();
            if (now - lastCursorEmitRef.current > 40) { // throttle por tempo (~25/s), não por Math.random
                lastCursorEmitRef.current = now;
                const { x, y } = toBoardCoords(e.clientX, e.clientY);
                socketRef.current.emit('cursor-move', { boardId, user: (user && user.nome) || 'Usuário', x, y });
            }
        }

        // Preview do conector segue o mouse enquanto liga as duas formas
        if (connectFrom) setConnectMouse(toBoardCoords(e.clientX, e.clientY));

        if (isPanning) {
            const dx = e.clientX - panStart.x;
            const dy = e.clientY - panStart.y;
            setOffset(prev => ({ x: prev.x + dx, y: prev.y + dy }));
            setPanStart({ x: e.clientX, y: e.clientY });
        }

        // Caixa de seleção (marquee) em andamento
        if (isSelectingRef.current) {
            const p = toBoardCoords(e.clientX, e.clientY);
            const s = selStartRef.current;
            const rect = { x: Math.min(s.x, p.x), y: Math.min(s.y, p.y), w: Math.abs(p.x - s.x), h: Math.abs(p.y - s.y) };
            selRectRef.current = rect;
            setSelectionRect(rect);
            return;
        }

        // Arrastar — move TODO o grupo selecionado
        if (isDragging && selectedIds.length) {
            const { x, y } = toBoardCoords(e.clientX, e.clientY);
            let dx = x - dragStart.x;
            let dy = y - dragStart.y;
            const starts = dragGroupStartRef.current;
            const bbox = dragGroupBBoxRef.current;
            const targets = snapTargetsRef.current;
            let gx = null, gy = null;
            if (bbox && targets) {
                const TH = 6 / scale; // tolerância de snap (coords do board)
                const cx = (bbox.minX + bbox.maxX) / 2;
                const cy = (bbox.minY + bbox.maxY) / 2;
                // Eixo X: esquerda/centro/direita do grupo grudam nas linhas verticais dos outros
                let best = TH, adj = 0;
                [bbox.minX + dx, cx + dx, bbox.maxX + dx].forEach(c => targets.xs.forEach(t => { const d = t - c; if (Math.abs(d) < best) { best = Math.abs(d); adj = d; gx = t; } }));
                if (gx != null) dx += adj;
                // Eixo Y: topo/centro/base
                best = TH; adj = 0;
                [bbox.minY + dy, cy + dy, bbox.maxY + dy].forEach(c => targets.ys.forEach(t => { const d = t - c; if (Math.abs(d) < best) { best = Math.abs(d); adj = d; gy = t; } }));
                if (gy != null) dy += adj;
            }
            setGuides({ x: gx, y: gy });
            setElements(prev => prev.map(el =>
                starts[el.id] ? { ...el, x: starts[el.id].x + dx, y: starts[el.id].y + dy } : el
            ));
        }

        // Redimensionar elemento (a partir de qualquer um dos 4 cantos)
        if (isResizing && selectedElementId) {
            const { x, y } = toBoardCoords(e.clientX, e.clientY);
            const dx = x - resizeStart.x;
            const dy = y - resizeStart.y;
            const s = resizeElementStart;
            const c = resizeCorner;
            let nx = s.x, ny = s.y, nw = s.width, nh = s.height;
            if (c.includes('e')) nw = Math.max(20, s.width + dx);
            if (c.includes('s')) nh = Math.max(20, s.height + dy);
            if (c.includes('w')) { nw = Math.max(20, s.width - dx); nx = s.x + (s.width - nw); }
            if (c.includes('n')) { nh = Math.max(20, s.height - dy); ny = s.y + (s.height - nh); }

            setElements(prev => prev.map(el =>
                el.id === selectedElementId ? { ...el, x: nx, y: ny, width: nw, height: nh } : el
            ));
        }
    };

    const handleMouseUp = async () => {
        // Finalizar caixa de seleção (marquee): seleciona o que estiver dentro
        if (isSelectingRef.current) {
            isSelectingRef.current = false;
            const r = selRectRef.current;
            selRectRef.current = null;
            setSelectionRect(null);
            if (r && (r.w > 3 || r.h > 3)) {
                const hits = elements.filter(el =>
                    el.type !== 'connector' &&
                    el.x < r.x + r.w && el.x + el.width > r.x &&
                    el.y < r.y + r.h && el.y + el.height > r.y
                ).map(el => el.id);
                setSelectedIds(hits);
            }
            setIsPanning(false);
            return;
        }

        // Salvar posição de TODOS os elementos arrastados (grupo + filhos de frame)
        if (isDragging && selectedIds.length) {
            const ids = dragMovingIdsRef.current.length ? dragMovingIdsRef.current : [...selectedIds];
            for (const id of ids) {
                const element = elements.find(el => el.id === id);
                if (element) {
                    try {
                        await axios.put(`${API_URL}/elements/${id}`, { x: element.x, y: element.y });
                        if (socketRef.current) socketRef.current.emit('element-update', { boardId, element });
                    } catch (err) {
                        console.error('Erro ao salvar posição:', err);
                    }
                }
            }
        }

        // Salvar posição+tamanho do elemento redimensionado
        if (isResizing && selectedElementId) {
            const element = elements.find(el => el.id === selectedElementId);
            if (element) {
                try {
                    await axios.put(`${API_URL}/elements/${selectedElementId}`, {
                        x: element.x, y: element.y, width: element.width, height: element.height
                    });
                    if (socketRef.current) socketRef.current.emit('element-update', { boardId, element });
                } catch (err) {
                    console.error('Erro ao salvar tamanho:', err);
                }
            }
        }

        setIsPanning(false);
        setIsDragging(false);
        setIsResizing(false);
        setGuides({ x: null, y: null });
        // Fecha o passo de desfazer do drag/resize (só se algo realmente mudou)
        if (pendingBeforeRef.current) { pushUndoIfChanged(pendingBeforeRef.current); pendingBeforeRef.current = null; }
    };


    // Image Upload
    const handleImageUpload = async (e) => {
        const file = e.target.files[0];
        if (!file) return;

        const formData = new FormData();
        formData.append('image', file);
        formData.append('board_id', boardId);

        try {
            const res = await axios.post(`${API_URL}/upload`, formData);
            const { filename, url } = res.data.data;

            // Creates the image element on the board
            // Default to center of screen approx
            const centerX = (-offset.x + window.innerWidth / 2) / scale;
            const centerY = (-offset.y + window.innerHeight / 2) / scale;

            const newElement = {
                board_id: boardId,
                type: 'image',
                x: centerX,
                y: centerY,
                width: 300, // Default width
                height: 300, // Default height - should really respect aspect ratio but simplification for now
                content: { filename, url }
            };

            await createElement(newElement);

        } catch (err) {
            console.error('Erro upload:', err);
            alert('Erro ao enviar imagem');
        }

        // Clear input
        e.target.value = '';
    };

    // Iniciar arrasto de elemento
    const startDrag = (e, el) => {
        if (activeTool === 'connector') { e.stopPropagation(); handleConnectClick(el); return; }
        if (activeTool !== 'select') return;
        e.stopPropagation();

        const { x, y } = toBoardCoords(e.clientX, e.clientY);

        // Shift = alterna o elemento no grupo (e não inicia arrasto)
        if (e.shiftKey) {
            setSelectedIds(prev => prev.includes(el.id) ? prev.filter(id => id !== el.id) : [...prev, el.id]);
            return;
        }

        // Clicar num já-selecionado mantém o grupo (arrasta todos); senão seleciona só ele
        let groupIds = selectedIds.includes(el.id) ? selectedIds : [el.id];
        if (!selectedIds.includes(el.id)) setSelectedIds(groupIds);

        // Se arrastar um FRAME, leva junto o que está dentro dele (movingIds) — seleção continua só no frame
        let movingIds = [...groupIds];
        groupIds.forEach(gid => {
            const g = elements.find(x => x.id === gid);
            if (g && g.type === 'frame') {
                elements.forEach(child => {
                    if (child.id !== gid && child.type !== 'connector' && child.type !== 'frame' && !movingIds.includes(child.id) && isInsideFrame(child, g)) movingIds.push(child.id);
                });
            }
        });
        dragMovingIdsRef.current = movingIds;

        // Guardar posição inicial de todos que vão se mover
        const starts = {};
        movingIds.forEach(id => { const g = elements.find(x => x.id === id); if (g) starts[id] = { x: g.x, y: g.y }; });
        dragGroupStartRef.current = starts;

        // bbox do que se move (início) + alvos de snap = bordas/centros dos demais (exceto conectores)
        let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity;
        movingIds.forEach(id => { const g = elements.find(x => x.id === id); if (g) { minX = Math.min(minX, g.x); minY = Math.min(minY, g.y); maxX = Math.max(maxX, g.x + g.width); maxY = Math.max(maxY, g.y + g.height); } });
        dragGroupBBoxRef.current = { minX, minY, maxX, maxY };
        const xs = [], ys = [];
        elements.forEach(other => {
            if (movingIds.includes(other.id) || other.type === 'connector') return;
            xs.push(other.x, other.x + other.width / 2, other.x + other.width);
            ys.push(other.y, other.y + other.height / 2, other.y + other.height);
        });
        snapTargetsRef.current = { xs, ys };

        pendingBeforeRef.current = cloneEls(elementsRef.current); // snapshot p/ desfazer (commit no mouseUp se mudou)
        setIsDragging(true);
        setDragStart({ x, y });
    };

    // Iniciar redimensionamento
    const startResize = (e, el, corner = 'se') => {
        e.stopPropagation();
        e.preventDefault();

        const { x, y } = toBoardCoords(e.clientX, e.clientY);
        setSelectedIds([el.id]);
        pendingBeforeRef.current = cloneEls(elementsRef.current); // snapshot p/ desfazer (commit no mouseUp se mudou)
        setIsResizing(true);
        setResizeCorner(corner);
        setResizeStart({ x, y });
        setResizeElementStart({ x: el.x, y: el.y, width: el.width, height: el.height });
    };

    // Z-Index baseado no tipo (shapes atrás de imagens)
    const getZIndex = (el) => {
        if (el.type === 'frame') return 1; // frames ao fundo, atrás de tudo
        if (el.type === 'shape') return 10 + (el.z_index || 0);
        if (el.type === 'image') return 100 + (el.z_index || 0);
        if (el.type === 'sticky') return 200 + (el.z_index || 0);
        if (el.type === 'text') return 300 + (el.z_index || 0);
        return el.z_index || 1;
    };

    // Handles de redimensionamento (4 cantos)
    const ResizeHandles = ({ el }) => (
        <>
            {['nw', 'ne', 'sw', 'se'].map(corner => (
                <div
                    key={corner}
                    onMouseDown={(e) => startResize(e, el, corner)}
                    style={{
                        position: 'absolute',
                        width: 12,
                        height: 12,
                        background: '#ffffff',
                        border: '2px solid #2563eb',
                        borderRadius: '50%',
                        zIndex: 9999,
                        ...(corner.includes('n') ? { top: -7 } : { bottom: -7 }),
                        ...(corner.includes('w') ? { left: -7 } : { right: -7 }),
                        cursor: (corner === 'nw' || corner === 'se') ? 'nwse-resize' : 'nesw-resize'
                    }}
                />
            ))}
        </>
    );

    // ---- Conectores (vinculam duas formas e seguem quando elas movem) ----
    // Ponto na borda do elemento na direção de (tx,ty) — faz a linha tocar a borda, não o centro
    const edgePoint = (el, tx, ty) => {
        const cx = el.x + el.width / 2, cy = el.y + el.height / 2;
        const dx = tx - cx, dy = ty - cy;
        if (dx === 0 && dy === 0) return { x: cx, y: cy };
        const sx = dx !== 0 ? (el.width / 2) / Math.abs(dx) : Infinity;
        const sy = dy !== 0 ? (el.height / 2) / Math.abs(dy) : Infinity;
        const s = Math.min(sx, sy);
        return { x: cx + dx * s, y: cy + dy * s };
    };

    // Elemento está "dentro" do frame? (centro dentro dos limites do frame)
    const isInsideFrame = (child, frame) => {
        const cx = child.x + child.width / 2, cy = child.y + child.height / 2;
        return cx >= frame.x && cx <= frame.x + frame.width && cy >= frame.y && cy <= frame.y + frame.height;
    };

    // Ferramenta conector: 1º clique = origem, 2º clique = destino → cria o conector
    const handleConnectClick = (el) => {
        if (!connectFrom) { setConnectFrom(el.id); return; }
        if (connectFrom === el.id) { setConnectFrom(null); return; }
        createElement({ board_id: boardId, type: 'connector', x: 0, y: 0, width: 0, height: 0, z_index: 0, content: { fromId: connectFrom, toId: el.id, color: '#64748b' } });
        setConnectFrom(null);
        setActiveTool('select');
    };

    const renderConnector = (c) => {
        const a = elements.find(e => e.id === (c.content && c.content.fromId));
        const b = elements.find(e => e.id === (c.content && c.content.toId));
        if (!a || !b) return null; // não renderiza conector com ponta faltando (dangling)
        const ac = { x: a.x + a.width / 2, y: a.y + a.height / 2 };
        const bc = { x: b.x + b.width / 2, y: b.y + b.height / 2 };
        const p1 = edgePoint(a, bc.x, bc.y);
        const p2 = edgePoint(b, ac.x, ac.y);
        const s1 = { x: p1.x * scale + offset.x, y: p1.y * scale + offset.y };
        const s2 = { x: p2.x * scale + offset.x, y: p2.y * scale + offset.y };
        const sel = selectedIds.includes(c.id);
        const col = sel ? '#2563eb' : ((c.content && c.content.color) || '#64748b');
        const ang = Math.atan2(s2.y - s1.y, s2.x - s1.x);
        const ah = 11;
        const ax1 = s2.x - ah * Math.cos(ang - Math.PI / 7), ay1 = s2.y - ah * Math.sin(ang - Math.PI / 7);
        const ax2 = s2.x - ah * Math.cos(ang + Math.PI / 7), ay2 = s2.y - ah * Math.sin(ang + Math.PI / 7);
        return (
            <g key={c.id}>
                <line x1={s1.x} y1={s1.y} x2={s2.x} y2={s2.y} stroke="transparent" strokeWidth={16} style={{ pointerEvents: 'stroke', cursor: 'pointer' }} onMouseDown={(e) => { e.stopPropagation(); setSelectedIds([c.id]); }} />
                <line x1={s1.x} y1={s1.y} x2={s2.x} y2={s2.y} stroke={col} strokeWidth={sel ? 3 : 2} style={{ pointerEvents: 'none' }} />
                <polygon points={`${s2.x},${s2.y} ${ax1},${ay1} ${ax2},${ay2}`} fill={col} style={{ pointerEvents: 'none' }} />
            </g>
        );
    };

    // Element Rendering Helpers
    const renderElement = (el) => {

        const style = {
            position: 'absolute',
            left: `${el.x}px`,
            top: `${el.y}px`,
            width: `${el.width}px`,
            height: `${el.height}px`,
            transform: `rotate(${el.rotation || 0}deg)`,
            zIndex: getZIndex(el),
            cursor: activeTool === 'pan' ? 'grab' : activeTool === 'select' ? 'move' : 'default'
        };

        if (selectedIds.includes(el.id)) {
            style.border = '2px solid #2563eb';
            style.boxShadow = '0 0 0 2px rgba(37, 99, 235, 0.25)';
        }

        const content = el.content || {};

        // Frame (moldura que organiza; arrastar leva o conteúdo de dentro junto)
        if (el.type === 'frame') {
            const isSel = selectedIds.includes(el.id);
            return (
                <div
                    key={el.id}
                    className={styles.element}
                    style={{ ...style, background: 'rgba(148,163,184,0.07)', borderRadius: 6, ...(isSel ? {} : { border: '1px solid #cbd5e1' }) }}
                    onMouseDown={(e) => startDrag(e, el)}
                >
                    <input
                        value={content.title || 'Frame'}
                        onChange={(e) => updateLocalElement(el.id, { content: { ...content, title: e.target.value } })}
                        onBlur={() => saveElement(el)}
                        onMouseDown={(e) => { e.stopPropagation(); setSelectedIds([el.id]); }}
                        style={{ position: 'absolute', top: -24, left: 0, border: 'none', background: 'transparent', color: '#64748b', fontSize: 13, fontWeight: 600, fontFamily: 'inherit', outline: 'none', maxWidth: '100%', padding: 0, cursor: 'text' }}
                    />
                    {el.id === selectedElementId && <ResizeHandles el={el} />}
                </div>
            );
        }

        // Sticky Note
        if (el.type === 'sticky') {
            return (
                <div
                    key={el.id}
                    className={styles.element}
                    style={{ ...style, backgroundColor: content.color || '#fff740', padding: '15px', boxShadow: '2px 2px 5px rgba(0,0,0,0.2)' }}
                    onMouseDown={(e) => startDrag(e, el)}
                >
                    <textarea
                        style={{
                            width: '100%',
                            height: '100%',
                            background: 'transparent',
                            border: 'none',
                            resize: 'none',
                            fontSize: '16px',
                            fontFamily: 'Caveat, cursive',
                            cursor: 'text' // Garante que o cursor de texto apareça no hover da área de texto
                        }}
                        value={content.text || ''}
                        onChange={(e) => updateLocalElement(el.id, { content: { ...content, text: e.target.value } })}
                        onBlur={() => saveElement(el)}
                        onMouseDown={(e) => { e.stopPropagation(); setSelectedIds([el.id]); }} // Seleciona ao clicar no texto também
                    />
                    {/* Permitir arrastar clicando na borda (padding) que o onMouseDown da div captura */}
                    {el.id === selectedElementId && <ResizeHandles el={el} />}
                </div>
            );
        }

        // Text
        if (el.type === 'text') {
            return (
                <div
                    key={el.id}
                    className={styles.element}
                    style={style}
                    onMouseDown={(e) => startDrag(e, el)}
                >
                    <textarea
                        style={{
                            width: '100%',
                            height: '100%',
                            background: 'transparent',
                            border: 'none',
                            resize: 'none',
                            fontSize: `${Math.max(12, el.height * 0.5)}px`, // Fonte escala com a altura
                            lineHeight: '1',
                            color: '#333',
                            cursor: 'text'
                        }}
                        value={content.text || ''}
                        onChange={(e) => updateLocalElement(el.id, { content: { ...content, text: e.target.value } })}
                        onBlur={() => saveElement(el)}
                        onMouseDown={(e) => { e.stopPropagation(); setSelectedIds([el.id]); }}
                        onKeyDown={(e) => {
                            if (e.key === 'Escape') {
                                e.target.blur(); // Sai do modo de edição
                                e.preventDefault(); // Evita perder a seleção do elemento
                            }
                        }}
                    />
                    {el.id === selectedElementId && <ResizeHandles el={el} />}
                </div>
            );
        }

        // Shapes
        if (el.type === 'shape') {
            const shp = content.shape;
            // Losango e triângulo: SVG (preenche o bbox, borda constante)
            if (shp === 'diamond' || shp === 'triangle') {
                const pts = shp === 'diamond' ? '50,2 98,50 50,98 2,50' : '50,4 97,96 3,96';
                return (
                    <div key={el.id} className={styles.element} style={style} onMouseDown={(e) => startDrag(e, el)}>
                        <svg width="100%" height="100%" viewBox="0 0 100 100" preserveAspectRatio="none" style={{ display: 'block' }}>
                            <polygon points={pts} fill={content.color || '#fff'} stroke={content.border || '#000'} strokeWidth="2" vectorEffect="non-scaling-stroke" />
                        </svg>
                        {el.id === selectedElementId && <ResizeHandles el={el} />}
                    </div>
                );
            }
            const isCircle = shp === 'circle';
            return (
                <div
                    key={el.id}
                    className={styles.element}
                    style={{
                        ...style,
                        backgroundColor: content.color || '#fff',
                        border: `2px solid ${content.border || '#000'}`,
                        borderRadius: isCircle ? '50%' : '0'
                    }}
                    onMouseDown={(e) => startDrag(e, el)}
                >
                    {el.id === selectedElementId && <ResizeHandles el={el} />}
                </div>
            );
        }

        // Images
        if (el.type === 'image') {
            return (
                <div
                    key={el.id}
                    className={styles.element}
                    style={style}
                    onMouseDown={(e) => startDrag(e, el)}
                >
                    <img
                        src={`${UPLOAD_BASE}${content.url}`}
                        alt="Upload"
                        style={{ width: '100%', height: '100%', objectFit: 'contain', pointerEvents: 'none' }}
                        draggable={false}
                    />
                    {el.id === selectedElementId && <ResizeHandles el={el} />}
                </div>
            );
        }

        return null;
    };

    const updateLocalElement = (id, changes) => {
        setElements(prev => prev.map(el => el.id === id ? { ...el, ...changes } : el));
    };

    const saveElement = async (el) => {
        // Find latest version from state to save
        const currentEl = elements.find(e => e.id === el.id);
        if (!currentEl) return;

        try {
            await axios.put(`${API_URL}/elements/${el.id}`, currentEl);
            if (socketRef.current) {
                socketRef.current.emit('element-update', { boardId, element: currentEl });
            }
        } catch (err) {
            console.error('Erro ao salvar elemento:', err);
        }
    };

    // --------------------------------------------------------------------------------
    // RENDERIZAÇÃO
    // --------------------------------------------------------------------------------

    // LISTA DE BOARDS
    if (!boardId) {
        return (
            <div className={styles.container}>
                <div className={styles.header}>
                    <div className={styles.titleSection}>
                        <h1 style={{ margin: 0, fontSize: '24px' }}>Quadros Brancos</h1>
                    </div>
                    <div className={styles.actions}>
                        <button className={`${styles.actionButton} ${styles.primaryButton}`} onClick={createBoard}>
                            + Novo Quadro
                        </button>
                    </div>
                </div>

                <div className={styles.dashboardList}>
                    {loading ? <p>Carregando...</p> : (
                        <div className={styles.grid}>
                            <div className={styles.createCard} onClick={createBoard}>
                                <span style={{ fontSize: '40px' }}>+</span>
                                <span>Criar Novo Quadro</span>
                            </div>

                            {boards.map(board => (
                                <div key={board.id} className={styles.boardCard} onClick={() => navigate(`/quadro-branco/${board.id}`)}>
                                    <div className={styles.cardPreview}>🎨</div>
                                    <div className={styles.cardFooter}>
                                        <div>
                                            <h3 className={styles.cardTitle}>{board.name}</h3>
                                            <span className={styles.cardDate}>
                                                {new Date(board.updated_at).toLocaleDateString()}
                                            </span>
                                        </div>
                                    </div>
                                    <button className={styles.deleteBtn} onClick={(e) => deleteBoard(e, board.id)}>
                                        🗑️
                                    </button>
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            </div>
        );
    }

    // CANVAS DE EDIÇÃO
    return (
        <div className={styles.container}>
            {/* HEADER DO BOARD */}
            <div className={styles.header}>
                <div className={styles.titleSection}>
                    <button className={styles.backButton} onClick={() => navigate('/quadro-branco')}>
                        ⬅
                    </button>
                    <input
                        className={styles.boardTitleInput}
                        value={currentBoard?.name || ''}
                        onChange={(e) => setCurrentBoard({ ...currentBoard, name: e.target.value })}
                        onBlur={async () => {
                            try {
                                await axios.put(`${API_URL}/boards/${boardId}`, { name: currentBoard.name });
                            } catch (e) { console.error('Error updating name', e); }
                        }}
                    />
                </div>
                <div className={styles.actions}>
                    <button className={styles.actionButton} onClick={tidyUp} title="Organizar — selecione um FRAME + as fotos pra alinhar DENTRO dele; ou selecione fotos (ou nada = todas) pra alinhar no centro da tela">
                        <Icon.grid /> Organizar
                    </button>
                    <div style={{ display: 'flex', alignItems: 'center' }}>
                        <div title={(user && user.nome) || 'Você'} style={{ width: 30, height: 30, borderRadius: '50%', background: userColor((user && user.id) || 'me'), color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 12, fontWeight: 700, border: '2px solid #fff', boxShadow: '0 0 0 1px rgba(15,23,42,0.12)', position: 'relative', zIndex: 2 }}>{initials((user && user.nome) || 'V')}</div>
                        {Object.entries(remoteCursors).map(([id, c]) => (
                            <div key={id} title={c.user || 'Usuário'} style={{ width: 30, height: 30, borderRadius: '50%', background: userColor(id), color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 12, fontWeight: 700, border: '2px solid #fff', boxShadow: '0 0 0 1px rgba(15,23,42,0.12)', marginLeft: -8 }}>{initials(c.user)}</div>
                        ))}
                        <span style={{ marginLeft: 10, color: '#64748b', fontSize: 13 }}>{Object.keys(remoteCursors).length + 1} online</span>
                    </div>
                </div>
            </div>

            {/* CANVAS */}
            <div
                className={`${styles.canvasWrapper} ${activeTool === 'pan' || isPanning ? styles.panning : ''}`}
                onMouseDown={handleMouseDown}
                onMouseMove={handleMouseMove}
                onMouseUp={handleMouseUp}
                onMouseLeave={handleMouseUp}
                ref={canvasRefCallback}
                tabIndex={0} // Permitir foco para eventos de teclado
                style={{
                    outline: 'none',
                    backgroundColor: '#f7f8fa',
                    // Cursor: seta no selecionar, mãozinha no mover (grab/grabbing), cruz nas ferramentas de criar
                    cursor: activeTool === 'select' ? 'default' : activeTool === 'pan' ? (isPanning ? 'grabbing' : 'grab') : 'crosshair',
                    // Grade pontilhada infinita que acompanha o zoom e o pan (estilo Miro)
                    backgroundImage: 'radial-gradient(circle, #c6ccd6 1px, transparent 1px)',
                    backgroundSize: `${24 * scale}px ${24 * scale}px`,
                    backgroundPosition: `${offset.x}px ${offset.y}px`,
                }}
            >
                {/* CONECTORES — camada SVG (coords de tela, atrás dos elementos); recalcula das formas vivas → segue */}
                <svg style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', overflow: 'visible', pointerEvents: 'none' }}>
                    {elements.filter(el => el.type === 'connector').map(renderConnector)}
                    {connectFrom && connectMouse && (() => {
                        const a = elements.find(e => e.id === connectFrom);
                        if (!a) return null;
                        const p1 = edgePoint(a, connectMouse.x, connectMouse.y);
                        const s1 = { x: p1.x * scale + offset.x, y: p1.y * scale + offset.y };
                        const s2 = { x: connectMouse.x * scale + offset.x, y: connectMouse.y * scale + offset.y };
                        return <line x1={s1.x} y1={s1.y} x2={s2.x} y2={s2.y} stroke="#2563eb" strokeWidth={2} strokeDasharray="6 4" style={{ pointerEvents: 'none' }} />;
                    })()}
                </svg>

                <div
                    className={styles.canvasContent}
                    style={{ transform: `translate(${offset.x}px, ${offset.y}px) scale(${scale})` }}
                >
                    {elements.map(renderElement)}

                    {/* Caixa de seleção (marquee) */}
                    {selectionRect && (
                        <div style={{
                            position: 'absolute',
                            left: selectionRect.x,
                            top: selectionRect.y,
                            width: selectionRect.w,
                            height: selectionRect.h,
                            border: '1.5px solid #2563eb',
                            background: 'rgba(37, 99, 235, 0.08)',
                            zIndex: 5000,
                            pointerEvents: 'none'
                        }} />
                    )}

                    {/* Remote Cursors */}
                    {Object.entries(remoteCursors).map(([id, cursor]) => (
                        <div
                            key={id}
                            className={styles.userCursor}
                            style={{ left: cursor.x * scale + offset.x, top: cursor.y * scale + offset.y, transition: 'all 0.1s linear' }}
                        >
                            <svg
                                className={styles.cursorIcon}
                                viewBox="0 0 24 24"
                                fill={userColor(id)}
                                stroke="white"
                                strokeWidth="1"
                            >
                                <path d="M5.5 3.21l12.42 12.42-5.12 1.15-2.6 5.6-2.54-1.18 2.6-5.6-5.91-.72L5.5 3.21z" />
                            </svg>
                            <span className={styles.cursorLabel} style={{ background: userColor(id) }}>{cursor.user || 'Usuário'}</span>
                        </div>
                    ))}
                </div>

                {/* Guias de alinhamento (snapping) — coords de tela */}
                {guides.x != null && <div style={{ position: 'absolute', left: guides.x * scale + offset.x, top: 0, width: 1, height: '100%', background: '#f43f5e', zIndex: 90, pointerEvents: 'none' }} />}
                {guides.y != null && <div style={{ position: 'absolute', top: guides.y * scale + offset.y, left: 0, height: 1, width: '100%', background: '#f43f5e', zIndex: 90, pointerEvents: 'none' }} />}

                {/* DESFAZER / REFAZER (Ctrl+Z / Ctrl+Y) — canto superior esquerdo */}
                <div style={{ position: 'absolute', left: 16, top: 16, display: 'flex', gap: 2, background: '#ffffff', padding: 6, borderRadius: 10, boxShadow: '0 6px 24px rgba(15,23,42,0.14), 0 0 0 1px rgba(15,23,42,0.04)', zIndex: 96 }} onMouseDown={(e) => e.stopPropagation()}>
                    {(() => {
                        void histVersion; // re-render quando o histórico muda
                        const canUndo = histRef.current.past.length > 0;
                        const canRedo = histRef.current.future.length > 0;
                        const btn = (active) => ({ display: 'flex', alignItems: 'center', justifyContent: 'center', width: 34, height: 34, border: 'none', borderRadius: 8, background: 'transparent', color: active ? '#1e293b' : '#cbd5e1', cursor: active ? 'pointer' : 'default' });
                        return (
                            <>
                                <button onClick={undo} disabled={!canUndo} title="Desfazer (Ctrl+Z)" style={btn(canUndo)}><Icon.undo /></button>
                                <button onClick={redo} disabled={!canRedo} title="Refazer (Ctrl+Y)" style={btn(canRedo)}><Icon.redo /></button>
                            </>
                        );
                    })()}
                </div>

                {/* TOOLBAR */}
                <div className={styles.toolbar}>
                    <button className={`${styles.toolButton} ${activeTool === 'select' ? styles.active : ''}`} onClick={() => setActiveTool('select')} title="Selecionar (V)"><Icon.select /></button>
                    <button className={`${styles.toolButton} ${activeTool === 'pan' ? styles.active : ''}`} onClick={() => setActiveTool('pan')} title="Mover o quadro (H / Espaço)"><Icon.hand /></button>
                    <div className={styles.toolbarDivider}></div>
                    <button className={`${styles.toolButton} ${activeTool === 'sticky' ? styles.active : ''}`} onClick={() => setActiveTool('sticky')} title="Sticky note (N)"><Icon.sticky /></button>
                    <button className={`${styles.toolButton} ${activeTool === 'text' ? styles.active : ''}`} onClick={() => setActiveTool('text')} title="Texto (T)"><Icon.text /></button>
                    <div style={{ position: 'relative' }} onMouseDown={(e) => e.stopPropagation()}>
                        <button
                            className={`${styles.toolButton} ${['square', 'circle', 'diamond', 'triangle', 'connector'].includes(activeTool) ? styles.active : ''}`}
                            onClick={() => setShapesMenuOpen(o => !o)}
                            title="Formas e linhas"
                        ><Icon.shapes /></button>
                        {shapesMenuOpen && (
                            <div style={{ position: 'absolute', left: 'calc(100% + 12px)', top: 0, background: '#fff', borderRadius: 12, boxShadow: '0 8px 28px rgba(15,23,42,0.18), 0 0 0 1px rgba(15,23,42,0.05)', padding: 8, display: 'flex', flexDirection: 'column', gap: 2, zIndex: 200, minWidth: 200 }}>
                                {[
                                    { tool: 'square', label: 'Retângulo', kbd: 'R', icon: <Icon.square /> },
                                    { tool: 'circle', label: 'Oval', kbd: 'O', icon: <Icon.circle /> },
                                    { tool: 'diamond', label: 'Losango', icon: <Icon.diamond /> },
                                    { tool: 'triangle', label: 'Triângulo', icon: <Icon.triangle /> },
                                    { tool: 'connector', label: 'Seta', kbd: 'L', icon: <Icon.connector /> },
                                ].map(o => (
                                    <button key={o.tool} onClick={() => { if (o.tool === 'connector') setConnectFrom(null); setActiveTool(o.tool); setShapesMenuOpen(false); }} style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '8px 10px', border: 'none', background: activeTool === o.tool ? '#eef2ff' : 'transparent', borderRadius: 8, cursor: 'pointer', color: '#1e293b', fontSize: 14, textAlign: 'left', width: '100%' }}>
                                        <span style={{ display: 'flex', color: '#475569' }}>{o.icon}</span>
                                        <span style={{ flex: 1 }}>{o.label}</span>
                                        {o.kbd && <span style={{ color: '#94a3b8', fontSize: 12 }}>{o.kbd}</span>}
                                    </button>
                                ))}
                            </div>
                        )}
                    </div>
                    <button className={`${styles.toolButton} ${activeTool === 'frame' ? styles.active : ''}`} onClick={() => setActiveTool('frame')} title="Frame / moldura (F)"><Icon.frame /></button>
                    <div className={styles.toolbarDivider}></div>
                    <label className={styles.toolButton} title="Cor de preenchimento" style={{ position: 'relative', cursor: 'pointer' }}>
                        <span className={styles.colorSwatch} style={{ background: selectedElementId ? (elements.find(el => el.id === selectedElementId)?.content?.color || defaultColor) : defaultColor }} />
                        <input
                            type="color"
                            value={selectedElementId ? (elements.find(el => el.id === selectedElementId)?.content?.color || defaultColor) : defaultColor}
                            onChange={(e) => changeElementColor(e.target.value)}
                            style={{ position: 'absolute', inset: 0, opacity: 0, cursor: 'pointer' }}
                        />
                    </label>
                    <button className={styles.toolButton} onClick={() => fileInputRef.current?.click()} title="Inserir imagem (I)"><Icon.image /></button>
                    <input type="file" ref={fileInputRef} style={{ display: 'none' }} accept="image/*" onChange={handleImageUpload} />
                    <div className={styles.toolbarDivider}></div>
                    <button className={styles.toolButton} onClick={tidyUp} title="Organizar — alinha as fotos num bloco (selecione um frame + fotos pra alinhar DENTRO dele)"><Icon.grid /></button>
                </div>

                {/* MINIMAPA */}
                {(() => {
                    const MW = 200, MH = 140, PAD = 60;
                    let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity;
                    elements.forEach(el => { minX = Math.min(minX, el.x); minY = Math.min(minY, el.y); maxX = Math.max(maxX, el.x + el.width); maxY = Math.max(maxY, el.y + el.height); });
                    const vw = (wrapperSize.w || 1) / scale, vh = (wrapperSize.h || 1) / scale;
                    const vx = -offset.x / scale, vy = -offset.y / scale;
                    minX = Math.min(minX, vx); minY = Math.min(minY, vy); maxX = Math.max(maxX, vx + vw); maxY = Math.max(maxY, vy + vh);
                    if (!isFinite(minX)) return null;
                    const ox = minX - PAD, oy = minY - PAD;
                    const worldW = (maxX - minX) + PAD * 2, worldH = (maxY - minY) + PAD * 2;
                    const ms = Math.min(MW / worldW, MH / worldH);
                    const onMini = (e) => {
                        const r = e.currentTarget.getBoundingClientRect();
                        const wx = (e.clientX - r.left) / ms + ox;
                        const wy = (e.clientY - r.top) / ms + oy;
                        setOffset({ x: (wrapperSize.w || 0) / 2 - wx * scale, y: (wrapperSize.h || 0) / 2 - wy * scale });
                    };
                    return (
                        <div onMouseDown={onMini} title="Minimapa — clique para navegar" style={{ position: 'absolute', left: 16, bottom: 16, width: MW, height: MH, background: 'rgba(255,255,255,0.96)', borderRadius: 10, boxShadow: '0 6px 24px rgba(15,23,42,0.14), 0 0 0 1px rgba(15,23,42,0.04)', overflow: 'hidden', zIndex: 95, cursor: 'pointer' }}>
                            {elements.map(el => (
                                <div key={el.id} style={{ position: 'absolute', left: (el.x - ox) * ms, top: (el.y - oy) * ms, width: Math.max(2, el.width * ms), height: Math.max(2, el.height * ms), background: (el.content && el.content.color) || (el.type === 'image' ? '#94a3b8' : '#cbd5e1'), borderRadius: (el.content && el.content.shape === 'circle') ? '50%' : 2 }} />
                            ))}
                            <div style={{ position: 'absolute', left: (vx - ox) * ms, top: (vy - oy) * ms, width: vw * ms, height: vh * ms, border: '1.5px solid #2563eb', background: 'rgba(37,99,235,0.10)', pointerEvents: 'none' }} />
                        </div>
                    );
                })()}

                {/* ZOOM CONTROLS */}
                <div className={styles.zoomControls}>
                    <button className={styles.zoomButton} onClick={() => setScale(s => Math.max(0.1, s - 0.1))} title="Diminuir zoom"><Icon.minus /></button>
                    <span className={styles.zoomLevel} onClick={() => setScale(1)} title="Voltar a 100%" style={{ cursor: 'pointer' }}>{Math.round(scale * 100)}%</span>
                    <button className={styles.zoomButton} onClick={() => setScale(s => Math.min(5, s + 0.1))} title="Aumentar zoom"><Icon.plus /></button>
                    <button className={styles.zoomButton} onClick={() => { setScale(1); setOffset({ x: 0, y: 0 }); }} title="Centralizar (reset)"><Icon.fit /></button>
                </div>
            </div>
        </div>
    );
};

export default WhiteboardPage;
